// Browser side of the network protocol.

import {
  PROTOCOL_VERSION,
  type ClientMessage,
  type CreateRoomOptions,
  type MapInfo,
  type PlayerAction,
  type AssetBundleInfo,
  type ServerMessage,
} from '../shared/net/protocol';
import type { UserCmd } from '../shared/physics/pmove';

export type RoomJoined = Extract<ServerMessage, { t: 'roomJoined' }> & { receivedAt?: number };

export class NetClient {
  id = 0;
  /** Stable player id of this guest identity. */
  pid = '';
  name = '';
  serverName = '';
  maps: MapInfo[] = [];
  /** Bundles every map uses (character model, player sounds); empty when the server has none. */
  assets: AssetBundleInfo[] = [];
  /** Set when the server created a new identity: the caller stores it. */
  newToken: string | null = null;
  /** The server lists this player as an admin (admin panel). */
  admin = false;
  private listeners: ((m: ServerMessage) => void)[] = [];
  private closed = false;
  private pingId = 0;
  onClose: ((reason: string) => void) | null = null;

  private constructor(
    private readonly ws: WebSocket,
    /** http(s) base URL of the server, for bundle and levelshot downloads. */
    readonly httpBase: string,
  ) {
    ws.onmessage = (ev) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(ev.data));
      } catch {
        return;
      }
      for (const fn of [...this.listeners]) fn(msg);
    };
    ws.onclose = () => {
      if (!this.closed) this.onClose?.('connection lost');
      this.closed = true;
    };
  }

  /** http(s) base of a ws(s) URL. */
  static httpBaseOf(url: string): string {
    return url.replace(/^ws/, 'http').replace(/\/+$/, '');
  }

  static connect(url: string, name: string, token: string | null, timeoutMs = 5000): Promise<NetClient> {
    return new Promise((resolve, reject) => {
      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch (err) {
        reject(err);
        return;
      }
      const client = new NetClient(ws, NetClient.httpBaseOf(url));
      const timer = setTimeout(() => {
        ws.close();
        reject(new Error(`no answer from ${url}`));
      }, timeoutMs);
      ws.onerror = () => {
        clearTimeout(timer);
        reject(new Error(`cannot connect to ${url}`));
      };
      ws.onopen = () => client.send({ t: 'hello', version: PROTOCOL_VERSION, name, token: token ?? undefined });
      const off = client.on((m) => {
        if (m.t === 'welcome') {
          clearTimeout(timer);
          off();
          client.id = m.id;
          client.pid = m.pid;
          client.name = m.name;
          client.serverName = m.serverName;
          client.maps = m.maps;
          client.assets = Array.isArray(m.assets) ? m.assets : [];
          client.newToken = m.token ?? null;
          client.admin = !!m.admin;
          resolve(client);
        } else if (m.t === 'error') {
          clearTimeout(timer);
          off();
          ws.close();
          reject(new Error(m.text));
        }
      });
    });
  }

  on(fn: (m: ServerMessage) => void): () => void {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter((f) => f !== fn);
    };
  }

  private send(m: ClientMessage): void {
    if (this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }

  private waitFor<T extends ServerMessage['t']>(t: T, pred: (m: Extract<ServerMessage, { t: T }>) => boolean = () => true, timeoutMs = 10000): Promise<Extract<ServerMessage, { t: T }>> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        off();
        reject(new Error(`server did not answer (${t})`));
      }, timeoutMs);
      const off = this.on((m) => {
        if (m.t === 'error') {
          clearTimeout(timer);
          off();
          reject(new Error(m.text));
        } else if (m.t === t && pred(m as Extract<ServerMessage, { t: T }>)) {
          clearTimeout(timer);
          off();
          resolve(m as Extract<ServerMessage, { t: T }>);
        }
      });
    });
  }

  /** The admin API (Authorization: Bearer token); throws the server's error text. */
  async adminRequest<T>(path: string, token: string, init: RequestInit = {}): Promise<T> {
    const res = await fetch(`${this.httpBase}/admin${path}`, { ...init, headers: { Authorization: `Bearer ${token}`, ...(init.headers ?? {}) } });
    const body = (await res.json().catch(() => ({}))) as T & { error?: string };
    if (!res.ok) throw new Error(body.error ?? `the server answered ${res.status}`);
    return body;
  }

  /** Room list updates on or off. */
  lobby(subscribe: boolean): void {
    this.send({ t: 'lobby', subscribe });
  }

  createRoom(room: CreateRoomOptions, msec: number): Promise<RoomJoined> {
    const joined = this.waitFor('roomJoined');
    this.send({ t: 'createRoom', room, msec });
    return joined;
  }

  joinRoom(target: { id?: string; code?: string }, msec: number): Promise<RoomJoined> {
    const joined = this.waitFor('roomJoined');
    this.send({ t: 'joinRoom', ...target, msec });
    return joined;
  }

  quickPlay(map: string | undefined, msec: number): Promise<RoomJoined> {
    const joined = this.waitFor('roomJoined');
    this.send({ t: 'quickPlay', map, msec });
    return joined;
  }

  /** Back to the lobby, still connected. */
  leaveRoom(): void {
    this.send({ t: 'leaveRoom' });
  }

  setRoomMap(map: string): void {
    this.send({ t: 'setRoomMap', map });
  }

  voteMap(map: string): void {
    this.send({ t: 'voteMap', map });
  }

  vote(yes: boolean): void {
    this.send({ t: 'vote', yes });
  }

  kick(player: number): void {
    this.send({ t: 'kick', player });
  }

  async setName(name: string): Promise<string> {
    const answer = this.waitFor('nameChanged');
    this.send({ t: 'setName', name });
    this.name = (await answer).name;
    return this.name;
  }

  sendCmd(cmd: UserCmd): void {
    this.send({ t: 'cmd', cmd });
  }

  sendAction(action: PlayerAction, slot = 0): void {
    this.send({ t: 'action', action, slot });
  }

  /** Join Axis (0) or Allies (1): the server respawns the player at the team's spawn, between the same two commands. */
  setTeam(team: 0 | 1): void {
    this.send({ t: 'setTeam', team });
  }

  /** Practice rooms: teleport to a player (goto) or bring them here (call). */
  teleport(kind: 'goto' | 'call', player: number): void {
    this.send({ t: kind, player });
  }

  /** Spectate a player (id, next / previous) or play again ('off'). */
  spectate(target: number | 'next' | 'prev' | 'off'): void {
    this.send({ t: 'spectate', target });
  }

  sendChat(text: string): void {
    this.send({ t: 'chat', text });
  }

  /** Round trip time to the server in ms (rejects when the server does not answer in time). */
  async ping(timeoutMs = 4000): Promise<number> {
    const id = ++this.pingId;
    const start = performance.now();
    const pong = this.waitFor('pong', (m) => m.id === id, timeoutMs);
    this.send({ t: 'ping', id });
    await pong;
    return performance.now() - start;
  }

  /** A JSON document of the server's HTTP API (null when it does not exist). */
  async getJson<T>(path: string): Promise<T | null> {
    const res = await fetch(`${this.httpBase}${path}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`the server answered ${res.status}`);
    return (await res.json()) as T;
  }

  async download(path: string): Promise<Blob | null> {
    const res = await fetch(`${this.httpBase}${path}`);
    return res.ok ? res.blob() : null;
  }

  /** Downloads with progress (0..1 of the expected size). */
  async downloadWithProgress(path: string, expectedSize: number, progress: (f: number) => void): Promise<Blob | null> {
    const res = await fetch(`${this.httpBase}${path}`);
    if (!res.ok) return null;
    const total = Number(res.headers.get('content-length')) || expectedSize;
    if (!res.body || !total) return res.blob();
    const reader = res.body.getReader();
    const chunks: Uint8Array<ArrayBuffer>[] = [];
    let got = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value as Uint8Array<ArrayBuffer>);
      got += value.length;
      progress(Math.min(1, got / total));
    }
    return new Blob(chunks);
  }

  close(): void {
    this.closed = true;
    this.ws.close();
  }
}
